<template>
  <div>
    <PageHeader
      title="Targets"
subtitle="Primary care practices in your territory, largest Medicare panels first. Practices already in your network are left out."
      :heading-level="1"
    >
      <template #actions>
        <FilterDropdown
          label="Territory"
          :icon="['fas', 'map']"
          :model-value="territory"
          :options="territoryOptions"
          placeholder="All states"
          clearable
          @update:model-value="onTerritory"
        />
      </template>
    </PageHeader>

    <div class="px-6 py-4">
      <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div class="flex flex-wrap items-start gap-4 px-4 py-3 border-b border-gray-100">
          <FilterTabs
            v-if="salesforceConnected"
            label="Practices from"
            :model-value="source"
            :options="sourceOptions"
            @update:model-value="onSource"
          />
          <FilterTabs
            label="Show"
            :model-value="targetsOnly ? 'targets' : 'all'"
            :options="showOptions"
            @update:model-value="targetsOnly = $event === 'targets'"
          />
          <FilterDropdown
            label="In an ACO today"
            :model-value="acoFilter"
            :options="acoOptions"
            placeholder="Any"
            clearable
            @update:model-value="acoFilter = $event"
          />
          <FilterDropdown
            label="Medicare patients"
            :model-value="sizeFilter"
            :options="sizeOptions"
            placeholder="Any size"
            clearable
            @update:model-value="sizeFilter = $event"
          />
          <label class="inline-flex flex-col gap-0.5 min-w-[220px]">
            <span class="text-xs font-semibold">Findings</span>
            <SimpleMultiSelect v-model="tagFilter" :options="tagOptions" placeholder="Any finding" searchable />
          </label>
          <button v-if="filtersActive" type="button" class="btn-link text-[13px] mt-6" @click="resetFilters">Reset</button>
        </div>

        <div class="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
          <span><span class="font-semibold text-gray-900">{{ filtered.length }}</span> practices shown</span>
          <span><span class="font-semibold text-gray-900">{{ filtered.reduce((a, r) => a + (r.lives || 0), 0).toLocaleString() }}</span> Medicare patients</span>
          <span><span class="font-semibold text-gray-900">{{ filtered.filter((r) => r.program).length }}</span> already in another ACO</span>
          <span v-if="salesforceConnected"><span class="font-semibold text-gray-900">{{ filtered.filter((r) => r.account).length }}</span> linked to Salesforce</span>
          <span v-if="buildId" class="ml-auto text-gray-400">Graph build {{ buildId.slice(0, 8) }}</span>
        </div>

        <LoadingIndicator v-if="loading" message="Loading targets..." />
        <div v-else-if="!rows.length" class="px-4 py-12 text-center">
          <div class="text-[13px] font-semibold text-gray-900">No briefs yet</div>
          <p class="text-[13px] text-gray-600 mt-1">Briefs are written by the growth brief batch. Once it has run for this territory, targets appear here.</p>
        </div>
        <div v-else-if="!filtered.length" class="px-4 py-12 text-center">
          <div class="text-[13px] font-semibold text-gray-900">Nothing matches these filters</div>
          <button type="button" class="btn-link text-[13px] mt-2" @click="resetFilters">Reset filters</button>
        </div>
        <AgGridVue
          v-else
          class="targets-grid"
          :theme="gridTheme"
          :column-defs="columnDefs"
          :default-col-def="defaultColDef"
          :row-data="filtered"
          :row-height="48"
          :header-height="40"
          dom-layout="autoHeight"
          :suppress-cell-focus="true"
          :get-row-id="getRowId"
          row-class="cursor-pointer"
          @row-clicked="onRowClicked"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue3'
import { themeQuartz } from 'ag-grid-community'
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue'
import FilterDropdown from '@/components/widgetcomponents/library/FilterDropdown.vue'
import FilterTabs from '@/components/widgetcomponents/library/FilterTabs.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue'


const TREND = {
  Tailwind: { bg: '#E3F3E0', fg: '#282C27' },
  Headwind: { bg: '#FDECEC', fg: '#D53D3D' },
  Neutral: { bg: '#F1F1F1', fg: '#626264' },
}


function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
}

export default {
  name: 'TargetsTab',
  components: { AgGridVue, PageHeader, FilterDropdown, FilterTabs, LoadingIndicator, SimpleMultiSelect },
  props: {
    initialTerritory: { type: String, default: null },
  },
  emits: ['open-brief', 'territory-change'],
  data() {
    return {
      rows: [],
      states: [],
      buildId: '',
      loading: false,
      territory: this.initialTerritory,
      targetsOnly: true,
      salesforceConnected: false,
      source: 'graph',
      sourceOptions: [
        { value: 'salesforce', label: 'Salesforce accounts' },
        { value: 'graph', label: 'Everything in the graph' },
      ],
      showOptions: [
        { value: 'targets', label: 'Targets' },
        { value: 'all', label: 'All practices' },
      ],
      acoFilter: null,
      acoOptions: [
        { value: 'none', label: 'Not in an ACO' },
        { value: 'any', label: 'In an ACO' },
      ],
      tagFilter: [],
      tagOptionsAll: [],
      sizeFilter: null,
      sizeOptions: [
        { value: 'small', label: 'Under 1,000' },
        { value: 'mid', label: '1,000 to 5,000' },
        { value: 'large', label: 'Over 5,000' },
      ],
      gridTheme: themeQuartz.withParams({
        browserColorScheme: 'light',
        headerBackgroundColor: '#F7F7F8',
        headerTextColor: '#626264',
        headerFontSize: 12,
        headerFontWeight: 600,
        fontSize: 13,
        rowBorder: true,
        wrapperBorder: false,
        wrapperBorderRadius: 0,
        cellHorizontalPadding: 16,
        rowHoverColor: '#F7F7F8',
      }),
      defaultColDef: {
        sortable: true,
        resizable: false,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        cellClass: 'cell-theme-left-aligned',
      },
      columnDefs: [
        { headerName: '#', field: 'rank', width: 56, sortable: false, cellClass: 'cell-theme-center-aligned text-gray-400 tabular-nums', headerClass: 'ag-center-aligned-header' },
        {
          headerName: 'Practice',
          field: 'name',
          flex: 2,
          minWidth: 240,
          cellRenderer: (p) => {
            const a = p.data.account
            const tag = a
              ? `<span class="inline-flex items-center justify-center w-4 h-4 rounded text-xs font-bold bg-blue-50 text-blue-700 ml-1.5 align-text-bottom" title="Linked to Salesforce ${a.kind === 'lead' ? 'lead' : 'account'} ${escapeHtml(a.name)}">S</span>`
              : ''
            return `<div class="leading-tight"><div class="font-semibold text-gray-900"><span class="sensitive">${escapeHtml(p.data.name)}</span>${tag}</div><div class="text-xs text-gray-500 sensitive">${escapeHtml(p.data.place)}</div></div>`
          },
        },
        { headerName: 'Providers', field: 'providers', width: 110, type: 'numericColumn', headerClass: 'ag-right-aligned-header', cellClass: 'cell-theme-right-aligned tabular-nums' },
        {
          headerName: 'Medicare patients',
          field: 'lives',
          width: 160,
          type: 'numericColumn',
          headerClass: 'ag-right-aligned-header',
          cellClass: 'cell-theme-right-aligned tabular-nums',
          valueFormatter: (p) => (p.value == null ? '' : p.value.toLocaleString()),
        },
        {
          headerName: 'Benchmark',
          colId: 'benchmark',
          width: 170,
          type: 'numericColumn',
          headerClass: 'ag-right-aligned-header',
          cellClass: 'cell-theme-right-aligned tabular-nums',
          valueGetter: (p) => (p.data.benchmark ? p.data.benchmark.benchmark : null),
          cellRenderer: (p) => {
            const b = p.data.benchmark
            if (!b) return '<span class="text-gray-300">Not delivered</span>'
            return `<span class="text-gray-900">$${Math.round(b.benchmark).toLocaleString()}</span><span class="text-xs text-gray-400 ml-1.5">${b.year} · ${escapeHtml(b.id_type)}</span>`
          },
        },
        {
          headerName: 'Benchmark trend',
          colId: 'trend',
          width: 150,
          sortable: false,
          cellRenderer: (p) => {
            const b = p.data.benchmark
            if (!b || !b.trend) return ''
            const t = TREND[b.trend] || TREND.Neutral
            return `<span class="rounded px-1.5 py-0.5 text-xs font-medium" style="background:${t.bg};color:${t.fg}">${escapeHtml(b.trend)}</span>`
          },
        },
        {
          headerName: 'ACO today',
          field: 'program',
          flex: 1,
          minWidth: 200,
          cellClass: 'cell-theme-left-aligned sensitive',
          cellRenderer: (p) => (p.value ? escapeHtml(p.value) : '<span class="text-gray-400">None</span>'),
        },
        {
          headerName: 'Owner',
          field: 'owner',
          width: 170,
          hide: true,
          colId: 'owner',
          cellClass: 'cell-theme-left-aligned sensitive',
          cellRenderer: (p) => (p.data.account ? `<div class="leading-tight"><div>${escapeHtml(p.data.account.owner_name || '')}</div><div class="text-xs text-gray-500">${escapeHtml(p.data.account.status || '')}</div></div>` : '<span class="text-gray-300">Not in Salesforce</span>'),
        },
        {
          headerName: '',
          field: 'practice_key',
          width: 48,
          sortable: false,
          resizable: false,
          cellClass: 'cell-theme-center-aligned',
          cellRenderer: () => '<span class="text-gray-400" title="Read brief" aria-label="Read brief"><svg width="8" height="12" viewBox="0 0 8 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1.5 1.5 6 6l-4.5 4.5"/></svg></span>',
        },
      ],
    }
  },
  computed: {
    territoryOptions() {
      return this.states.map((s) => ({ value: s, label: s }))
    },
    filtersActive() {
      return !this.targetsOnly || this.acoFilter !== null || this.sizeFilter !== null || this.tagFilter.length > 0
    },
    tagOptions() {
      const present = new Set(this.rows.flatMap((r) => r.tags || []))
      return this.tagOptionsAll.filter((t) => present.has(t.key)).map((t) => ({ value: t.key, label: t.label }))
    },
    filtered() {
      return this.rows
        .filter((t) => {
          if (this.targetsOnly && !t.is_target) return false
          if (this.acoFilter === 'none' && t.program) return false
          if (this.acoFilter === 'any' && !t.program) return false
          if (this.sizeFilter === 'small' && t.lives >= 1000) return false
          if (this.sizeFilter === 'mid' && (t.lives < 1000 || t.lives > 5000)) return false
          if (this.sizeFilter === 'large' && t.lives <= 5000) return false
          if (this.tagFilter.length && !this.tagFilter.every((k) => (t.tags || []).includes(k))) return false
          return true
        })
        .sort((a, b) => (b.lives || 0) - (a.lives || 0))
        .map((t, i) => ({ ...t, rank: i + 1 }))
    },
  },
  async created() {
    const { data } = await this.$axios.get('/api/growth/salesforce/status/')
    this.salesforceConnected = !!data.connected
    if (this.salesforceConnected) this.source = 'salesforce'
    this.columnDefs = this.columnDefs.map((c) => (c.colId === 'owner' ? { ...c, hide: !this.salesforceConnected } : c))
    this.load()
  },
  methods: {
    onSource(v) {
      this.source = v
      this.load()
    },
    async load() {
      this.loading = true
      try {
        const params = this.territory ? { state: this.territory } : {}
        if (this.source === 'salesforce') params.source = 'salesforce'
        const { data } = await this.$axios.get('/api/growth/briefs/', { params })
        this.buildId = data.build_id || ''
        this.states = data.states || []
        this.tagOptionsAll = data.tag_options || []
        this.rows = (data.rows || []).map((r) => ({
          ...r,
          place: [r.county, r.state].filter(Boolean).join(', '),
        }))
      } finally {
        this.loading = false
      }
    },
    onTerritory(v) {
      this.territory = v
      this.$emit('territory-change', v)
      this.load()
    },
    getRowId(p) {
      return p.data.practice_key
    },
    onRowClicked(e) {
      this.$emit('open-brief', e.data)
    },
    resetFilters() {
      this.acoFilter = null
      this.sizeFilter = null
      this.tagFilter = []
      this.targetsOnly = true
    },
  },
}
</script>

<style scoped>
.targets-grid :deep(.ag-center-cols-viewport) {
  min-height: 0 !important;
}
.targets-grid :deep(.ag-header-cell-text) {
  font-weight: 600;
}
.targets-grid :deep(.cell-theme-right-aligned) {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end;
  white-space: normal !important;
}
</style>
